import React, { useMemo } from "react";
import { VA_MAP, fullCountyName } from "../lib/counties";

export default function VAMap({ summary = {}, selected, onSelect }) {
    const { width, height, counties } = VA_MAP;

    const enriched = useMemo(
        () =>
            counties.map((c) => {
                const name = fullCountyName(c);
                return { ...c, fullName: name, count: summary[name] || 0 };
            }),
        [counties, summary],
    );

    // Show number badges on counties with events (sorted by most events - cap top ~15 to avoid clutter)
    const topBadges = useMemo(() => {
        return [...enriched]
            .filter((c) => c.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, 20);
    }, [enriched]);

    return (
        <div
            className="w-full h-full flex items-center justify-center relative"
            data-testid="va-map-wrapper"
        >
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full h-auto max-h-[70vh]"
                xmlns="http://www.w3.org/2000/svg"
                data-testid="va-map-svg"
            >
                <defs>
                    <filter
                        id="glow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                    >
                        <feGaussianBlur stdDeviation="3" result="glow" />
                        <feMerge>
                            <feMergeNode in="glow" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                <g>
                    {enriched.map((c) => {
                        const isSelected = selected === c.fullName;
                        const hasEvents = c.count > 0;
                        return (
                            <path
                                key={c.key}
                                d={c.d}
                                data-testid={`county-${c.key}`}
                                data-name={c.fullName}
                                className={[
                                    "va-county",
                                    hasEvents ? "has-events" : "",
                                    isSelected ? "selected" : "",
                                ].join(" ")}
                                onClick={() =>
                                    onSelect &&
                                    onSelect(
                                        isSelected ? null : c.fullName,
                                    )
                                }
                            >
                                <title>
                                    {c.fullName}
                                    {hasEvents
                                        ? ` — ${c.count} event${c.count > 1 ? "s" : ""}`
                                        : ""}
                                </title>
                            </path>
                        );
                    })}
                </g>

                <g pointerEvents="none">
                    {topBadges.map((c) => (
                        <g
                            key={`badge-${c.key}`}
                            transform={`translate(${c.cx},${c.cy})`}
                        >
                            <circle r="9" className="event-count-badge" />
                            <text className="event-count-text">
                                {c.count}
                            </text>
                        </g>
                    ))}
                </g>
            </svg>

            {/* Legend */}
            <div
                className="absolute bottom-2 left-2 flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-manrope"
                data-testid="map-legend"
            >
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 border border-zinc-700 bg-[#1a1a1a]" />
                    No events
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 border border-[#6b1e18] bg-[#3a1512]" />
                    Has events
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-[#FF3B30]" />
                    Selected
                </span>
            </div>
        </div>
    );
}
